import { CalendarViewType } from '../schema';
import { getCalendarViewTypeOptions } from '../utils/calendarViewTypeUtils';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import loc, { format } from 'owa-localize';
import { isEduStudent } from 'owa-bpos-utils';
import {
    switchToAgendaLabel,
    switchToDayLabel,
    switchToMonthLabel,
    switchToSchoolWeekLabel,
    switchToWeekLabel,
    switchToWorkWeekLabel,
} from './getMenuItems.locstring.json';
import {
    switchToDayText,
    oneDaySubMenuOptionText,
    multiDaysSubMenuOptionText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';

const MAX_DAYS_TO_SELECT = 7;

export function getMenuItems(
    selectedView: CalendarViewType,
    numDaysInDayView: number | undefined,
    onChangeView: (viewType: CalendarViewType) => void,
    onChangeNumDaysInDayView: (numDaysInDayView: number) => void
): IContextualMenuItem[] {
    return getCalendarViewTypeOptions().map(option => {
        if (option === CalendarViewType.Day) {
            return {
                key: 'numDaysInDayView',
                text:
                    option === selectedView && numDaysInDayView && numDaysInDayView >= 2
                        ? getDayViewLabel(numDaysInDayView)
                        : loc(switchToDayLabel),
                subMenuProps: {
                    items: createDayViewSubMenuItems(
                        numDaysInDayView,
                        MAX_DAYS_TO_SELECT,
                        onChangeNumDaysInDayView,
                        selectedView
                    ),
                },
                canCheck: true,
                checked: option === selectedView,
                split: true,
                onClick: () => {
                    if (selectedView !== option) {
                        onChangeNumDaysInDayView(1);
                    }
                },
            };
        }

        return {
            key: option,
            text: getLabel(option),
            canCheck: true,
            checked: option === selectedView,
            onClick: () => {
                if (selectedView !== option) {
                    onChangeView(option);
                }
            },
        };
    });
}

function getDayViewLabel(numOfDays: number | undefined): string {
    if (numOfDays === undefined) {
        return loc(switchToDayText);
    } else if (numOfDays === 1) {
        return format(loc(oneDaySubMenuOptionText), numOfDays);
    } else {
        return format(loc(multiDaysSubMenuOptionText), numOfDays);
    }
}

function createDayViewSubMenuItems(
    currentlySelectedNumDays: number | undefined,
    maxDaysToSelect: number,
    onChangeNumDaysInDayView: (numDaysInDayView: number) => void,
    selectedView: CalendarViewType
): IContextualMenuItem[] {
    const singleDayViewSubMenuOptions: IContextualMenuItem[] = [];
    for (let i = 0; i < maxDaysToSelect; i++) {
        const numOfDays = i + 1;
        let subMenuLabel = getDayViewLabel(numOfDays);
        singleDayViewSubMenuOptions.push({
            text: subMenuLabel,
            key: subMenuLabel,
            title: subMenuLabel,
            onRenderIcon: () => null,
            canCheck: true,
            checked:
                numOfDays === currentlySelectedNumDays && selectedView === CalendarViewType.Day,
            onClick: () => {
                onChangeNumDaysInDayView(numOfDays);
            },
        });
    }
    return singleDayViewSubMenuOptions;
}

function getLabel(viewType: CalendarViewType) {
    switch (viewType) {
        case CalendarViewType.Agenda:
            return loc(switchToAgendaLabel);
        case CalendarViewType.Day:
            return loc(switchToDayLabel);
        case CalendarViewType.Month:
            return loc(switchToMonthLabel);
        case CalendarViewType.Week:
            return loc(switchToWeekLabel);
        case CalendarViewType.WorkWeek:
            return isEduStudent() ? loc(switchToSchoolWeekLabel) : loc(switchToWorkWeekLabel);
        default:
            assertNever(viewType);
    }
}
